import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CalendarX2, Loader2, Search } from 'lucide-react';
import { moduleApi } from '../../lib/api';
import { Input } from '../ui/Input';

const fmtDate = (d?: string | null) => d ? new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';

type SortKey = 'date_desc' | 'date_asc';

// Historique des RDV Pro annulés — par le client depuis le mail de confirmation (motif indiqué) ou par
// l'établissement depuis l'app (compte à l'origine indiqué, un collaborateur pouvant annuler comme le
// compte principal). Distinction ajoutée le 2026-08-20 : avant ça, une annulation faite depuis l'app ne
// renseignait ni la date ni qui avait annulé, signalé par Emeni. Même gabarit que CRMContactCommandesPage.tsx.
export const RdvCancellationHistoryPage: React.FC = () => {
    const navigate = useNavigate();
    const [appointments, setAppointments] = React.useState<any[]>([]);
    const [loading, setLoading] = React.useState(true);
    const [q, setQ] = React.useState('');
    const [sort, setSort] = React.useState<SortKey>('date_desc');

    React.useEffect(() => {
        moduleApi.getCancelledProRdvAppointments()
            .then((res: any) => setAppointments(Array.isArray(res?.appointments) ? res.appointments : []))
            .catch(() => setAppointments([]))
            .finally(() => setLoading(false));
    }, []);

    const contactLabel = (a: any) => a.type === 'PROFESSIONNEL' ? (a.company_name || '') : `${a.first_name || ''} ${a.last_name || ''}`.trim();

    const filtered = React.useMemo(() => {
        const query = q.trim().toLowerCase();
        const list = query
            ? appointments.filter((a) => contactLabel(a).toLowerCase().includes(query) || String(a.subject || '').toLowerCase().includes(query) || String(a.cancel_reason || '').toLowerCase().includes(query))
            : appointments;
        return [...list].sort((a, b) => {
            const da = new Date(a.cancelled_at || 0).getTime();
            const db = new Date(b.cancelled_at || 0).getTime();
            return sort === 'date_asc' ? da - db : db - da;
        });
    }, [appointments, q, sort]);

    return (
        <div className="space-y-6">
            <header className="flex items-center gap-3">
                <button onClick={() => navigate('/rdv-pro')} className="p-2.5 rounded-xl border border-border text-content-muted hover:text-content hover:bg-surface-soft transition-all">
                    <ArrowLeft size={18} />
                </button>
                <div>
                    <h1 className="text-2xl font-bold tracking-tight text-content flex items-center gap-2"><CalendarX2 size={22} /> Historique des annulations</h1>
                    <p className="text-content-muted mt-1">Rendez-vous annulés, par le client (mail de confirmation) ou par l'établissement</p>
                </div>
            </header>

            <div className="flex items-center gap-2 max-w-2xl">
                <div className="relative flex-1">
                    <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-content-muted" />
                    <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un contact, un motif..." className="pl-8" />
                </div>
                <select value={sort} onChange={(e) => setSort(e.target.value as SortKey)}
                    className="bg-surface border border-border rounded-lg px-3 py-2 text-sm text-content outline-none focus:border-brand-500">
                    <option value="date_desc">Annulé le (récent → ancien)</option>
                    <option value="date_asc">Annulé le (ancien → récent)</option>
                </select>
            </div>

            {loading ? (
                <div className="flex justify-center py-16"><Loader2 className="animate-spin text-content-muted" size={28} /></div>
            ) : filtered.length === 0 ? (
                <p className="text-center text-content-muted py-12 bg-surface rounded-2xl border border-dashed border-border max-w-2xl">Aucun rendez-vous annulé.</p>
            ) : (
                <div className="space-y-3 max-w-2xl">
                    {filtered.map((a) => (
                        <div key={a.id} className="bg-surface p-4 rounded-2xl border border-border">
                            <div className="flex items-center justify-between gap-3">
                                <div className="min-w-0">
                                    <p className="text-sm font-bold text-content truncate">{contactLabel(a) || 'Contact inconnu'} {a.subject ? <span className="text-content-muted font-normal">— {a.subject}</span> : null}</p>
                                    <p className="text-xs text-content-muted mt-0.5">RDV prévu le {fmtDate(a.appointment_date)?.split(' à')[0] || a.appointment_date} à {a.start_time}</p>
                                </div>
                                <span className="text-[10px] font-bold px-2 py-1 rounded uppercase tracking-widest shrink-0 bg-red-500/10 text-red-500">Annulé</span>
                            </div>
                            <div className="mt-2.5 pt-2.5 border-t border-border space-y-1">
                                <p className="text-xs text-content-muted">
                                    {a.cancelled_at ? `Annulé le ${fmtDate(a.cancelled_at)}` : 'Date d\'annulation inconnue'}
                                    {a.cancelled_by_type === 'client' && <span className="ml-1.5 font-bold text-content">— par le client</span>}
                                    {a.cancelled_by_type === 'establishment' && <span className="ml-1.5 font-bold text-content">— par l'établissement ({a.cancelled_by_name || 'compte inconnu'})</span>}
                                </p>
                                {a.cancel_reason && (
                                    <p className="text-sm text-content bg-surface-soft border border-border rounded-lg px-3 py-2 mt-1">{a.cancel_reason}</p>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default RdvCancellationHistoryPage;
